import React, { useCallback, useEffect, useRef } from "react";
import { throttle } from "throttle-debounce";
import { sendReaction } from '../../../../../api/player';
import { EMOJI_BY_CODE_MAP, MESSAGE_EMOJI_SRC } from "../../../constants/components/emoji";
import { Event } from "../../../../../cp/components/Comet";
import "./style.scss";

const DELAY_SEND_REACTION_MS = 1000;
const MAX_NUMBER_ONE_EMOJI_PER_SECOND = 10;
const MAX_ANIMATIONS = 20;

function getRandomNumber(min = 0, max = 10) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

const Reactions = ({ eventKey, streamId, playerContainerRef, isHidden, reactions, onClick, active }) => {
    const containerReactionsRef = useRef();
    const containerSizeRef = useRef();
    const timerRef = useRef(null);
    const mouseEnteredRef = useRef(false);
    const intervalsRef = useRef({});
    const cashReactions = useRef({});
    const createdAtKeys = useRef([]);
    const activeAnimations = useRef(0);
    const streamIdRef = useRef(streamId);
    const activeRef = useRef(active);

    const className = "container-reactions";
    const numberReactions = reactions?.length;

    useEffect(() => {
        streamIdRef.current = streamId;
        activeRef.current = active;
    }, [streamId, active]);

    useEffect(() => {
        if (!playerContainerRef.current) return;

        const resizeObserver = new ResizeObserver(() => {
            const rect = playerContainerRef.current.getBoundingClientRect();
            const buttonWidth = window.innerWidth <= 1000 && (window.innerWidth <= 655 || window.innerWidth >= 900) ? 32 : 44;

            containerSizeRef.current = {
                height: rect.height,
                width: rect.width,
                containerReactionsWidth: buttonWidth * (numberReactions + 1),
            };
        });

        resizeObserver.observe(playerContainerRef.current);

        return () => {
            resizeObserver.disconnect();

            Object.keys(intervalsRef.current).forEach(interval => {
                clearInterval(interval);
                delete intervalsRef.current[interval];
            });

            if (timerRef.current) {
                clearTimeout(timerRef);
            }
        }
    }, [numberReactions]);

    const createReaction = (reaction) => {
        if (!activeRef.current || activeAnimations.current >= MAX_ANIMATIONS || !EMOJI_BY_CODE_MAP[reaction]) return;

        const imageSrc = MESSAGE_EMOJI_SRC(reaction);

        const emojiEl = document.createElement('div');
        emojiEl.className = 'emoji-animate';

        const imgEl = document.createElement('img');
        imgEl.src = imageSrc;

        emojiEl.appendChild(imgEl);

        const { height, width, containerReactionsWidth } = containerSizeRef.current;
        const left = width - getRandomNumber(44, containerReactionsWidth);

        emojiEl.style.left = `${left}px`;
        emojiEl.style.top = `${height - 164}px`;

        playerContainerRef.current.appendChild(emojiEl);
        activeAnimations.current++;

        emojiEl.style.setProperty('--final-y', `calc(-${height - 164}px)`);

        emojiEl.addEventListener('animationend', () => {
            emojiEl.remove();
            activeAnimations.current--;
        });
    }

    const onSendReaction = useCallback(throttle(DELAY_SEND_REACTION_MS, (reaction) => {
        createReaction(reaction);
        sendReaction(eventKey, streamId, reaction).catch(console.error);
    }), [eventKey, streamId]);

    const onStartProcessingReactions = () => {
        if (!activeRef.current) return;

        const key = createdAtKeys.current.shift();
        const reactions = cashReactions.current[key];

        reactions.forEach(data => {
            if (streamIdRef.current !== data.streamId) {
                return;
            }

            let quantity = Math.min(data.quantity, MAX_NUMBER_ONE_EMOJI_PER_SECOND);
            const ms = 1000 / quantity;

            const timer = setInterval(() => {
                if (--quantity < 0 || streamIdRef.current !== data.streamId) {
                    clearInterval(timer);
                    delete cashReactions.current[key];
                    delete intervalsRef.current[timer];
                    return;
                }

                createReaction(data.hash);
            }, ms);
            intervalsRef.current[timer] = key;
        });

        if (!createdAtKeys.current.length) {
            timerRef.current = null;
        } else {
            timerRef.current = setTimeout(onStartProcessingReactions, 1000);
        }
    };

    const onPatchReactions = (data) => {
        if (!activeRef.current || streamIdRef.current !== data.streamId) {
            return;
        }

        if (!cashReactions.current[data.createdAt]) {
            cashReactions.current[data.createdAt] = [data];
            createdAtKeys.current.push(data.createdAt);
        } else {
            cashReactions.current[data.createdAt].push(data);
        }

        if (timerRef.current === null) {
            timerRef.current = setTimeout(onStartProcessingReactions, 0);
        }
    };

    return (
        <div
            className={className}
            ref={containerReactionsRef}
            onClick={onClick}
        >
            <Event event={'create:reaction'} handler={onPatchReactions} />
            {!isHidden && (
                <div
                    className={`${className}-wrapper ${!active ? " is-collapsed" : ""}`}
                    onMouseEnter={() => mouseEnteredRef.current = true}
                    onMouseLeave={() => mouseEnteredRef.current = false}
                >
                    <div className={`${className}-content`}>
                        <div className={`${className}--controls`}>
                            {reactions.map(reaction =>
                                <button
                                    key={reaction}
                                    className={`${className}--button`}
                                    onClick={() => onSendReaction(reaction)}
                                >
                                    <img src={MESSAGE_EMOJI_SRC(reaction)} alt="" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default React.memo(Reactions);
